import { categories, games, type Game, type GameCategory } from "@/mocks/games";

export type SortKey = "hot" | "new" | "rating";

export interface GameFilters {
  category?: string;
  theme?: string;
  tags?: string[];
  keyword?: string;
}

export function getGameById(id: string): Game | undefined {
  return games.find((game) => game.id === id);
}

export function getCategoryByKey(key: string): GameCategory | undefined {
  return categories.find((category) => category.key === key);
}

export function getCategoryName(key: string): string {
  return getCategoryByKey(key)?.name ?? "全部游戏";
}

export function filterGames(list: Game[], filters: GameFilters): Game[] {
  const { category, theme, tags, keyword } = filters;
  return list.filter((game) => {
    if (category && game.category !== category) return false;
    if (theme && game.theme !== theme) return false;
    if (tags && tags.length > 0 && !tags.every((tag) => game.tags.includes(tag))) return false;
    if (keyword && keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      const haystack = [game.name, game.summary, game.description, game.theme, ...game.tags]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });
}

export function sortGames(list: Game[], sort: SortKey): Game[] {
  const result = [...list];
  if (sort === "hot") {
    result.sort((a, b) => b.players - a.players);
  } else if (sort === "new") {
    result.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  } else if (sort === "rating") {
    result.sort((a, b) => b.rating - a.rating);
  }
  return result;
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    page: safePage,
  };
}

export function getRelatedGames(game: Game, count = 4): Game[] {
  const sameCategory = games.filter((item) => item.id !== game.id && item.category === game.category);
  const sameTag = games.filter(
    (item) =>
      item.id !== game.id &&
      item.category !== game.category &&
      item.tags.some((tag) => game.tags.includes(tag)),
  );
  return [...sameCategory, ...sameTag].slice(0, count);
}

export function getPopularGames(count = 8): Game[] {
  return [...games].sort((a, b) => b.players - a.players).slice(0, count);
}

export function getLatestGames(count = 6): Game[] {
  return [...games].sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1)).slice(0, count);
}

export function formatPlayers(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)} 万人在玩`;
  }
  return `${value} 人在玩`;
}

export function formatDate(value: string): string {
  const [year, month, day] = value.split("-");
  return `${year} 年 ${Number(month)} 月 ${Number(day)} 日`;
}